// ============================================================================
// useMyActiveSessions - live list of tables the user is seated at OR watching
// ============================================================================
// Two queries in parallel:
//   1. tables where players array-contains uid  (seated sessions)
//   2. tables where the spectators array contains an entry for uid
//      (watching sessions - Firestore can't array-contains on object fields
//      so we query by spectatorUserIds, a mirror array maintained server-side
//      via the spectator endpoints. Falls back to scanning the spectators
//      array if the mirror isn't present.)
//
// Merged result drives the My Tables dock and the spectator banner's
// "back to my table" logic.
// ============================================================================
import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

export interface ActiveSession {
  tableId: string;
  name: string;
  isTournament: boolean;
  tournamentId?: string;
  status: string;
  // Role on this table
  role: 'seated' | 'spectating';
  // Seated-only fields
  myChips: number;
  myTurn: boolean;
  // Common
  currentPlayerName?: string;
  handInProgress: boolean;
}

export function useMyActiveSessions(currentUserId: string | null): ActiveSession[] {
  const [seated, setSeated] = useState<ActiveSession[]>([]);
  const [spectating, setSpectating] = useState<ActiveSession[]>([]);

  useEffect(() => {
    if (!currentUserId) { setSeated([]); return; }
    const q = query(
      collection(db, 'tables'),
      where('players', 'array-contains', currentUserId)
    );
    const unsub = onSnapshot(q, snap => {
      const rows: ActiveSession[] = [];
      snap.docs.forEach(d => {
        const data = d.data();
        const gs = data.gameState || {};
        const players: any[] = gs.players || [];
        const me = players.find(p => p && p.id === currentUserId);
        if (me?.eliminated) return;
        // Closed or cancelled tables don't belong in the dock, and neither
        // does a dead tournament seat. A busted seat must disappear even
        // while the TABLE's hand is still running (the old status gate kept
        // "chips 0 - hand in play" rows alive): zero chips with nothing in
        // the pot and not all-in means busted, full stop. An all-in player
        // is protected - their money is in the pot, not gone.
        if (data.status === 'ended' || data.tournamentCancelled) return;
        const isTourn = !!data.isTournament || !!data.tournamentId;
        const deadSeat = (me?.chips ?? 0) <= 0
          && me?.allIn !== true
          && (me?.currentBet ?? 0) <= 0
          && (me?.totalBetThisHand ?? 0) <= 0;
        if (isTourn && deadSeat) return;
        const currentIdx = gs.currentPlayerIndex;
        const currentP = (typeof currentIdx === 'number' && currentIdx >= 0 && currentIdx < players.length)
          ? players[currentIdx] : null;
        const myTurn = !!(currentP && currentP.id === currentUserId);
        const handInProgress = gs.status === 'playing' && gs.round && gs.round !== 'ended';
        // Show committed-stack (remaining chips + currentBet) so an all-in
        // player doesn't see "0" in the dock the moment they shove  -  their

        // money is in the pot, not lost. Once the hand resolves and the pot
        // pays out, this reduces back to the raw chip count for the next hand.
        const committed = (me?.chips ?? 0) + (me?.currentBet ?? 0);
        rows.push({
          tableId: d.id,
          name: data.name || d.id,
          isTournament: !!data.isTournament || !!data.tournamentId,
          tournamentId: data.tournamentId,
          status: gs.status || data.status || 'waiting',
          role: 'seated',
          myChips: committed,
          myTurn,
          currentPlayerName: currentP?.name,
          handInProgress: !!handInProgress,
        });
      });
      setSeated(rows);
    }, (err) => {
      console.warn('[useMyActiveSessions] seated subscription error:', err);
      setSeated([]);
    });
    return () => unsub();
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) { setSpectating([]); return; }
    // Watch tables and filter client-side. Firestore can't array-contains
    // an object's field, so we just read each table doc and check the
    // spectators array. To avoid scanning all tables we restrict by a
    // hopeful mirror field `spectatorUserIds`; if the server doesn't keep
    // that we'd add an empty list which is fine - fewer accidental matches.
    const q = query(
      collection(db, 'tables'),
      where('spectatorUserIds', 'array-contains', currentUserId)
    );
    const unsub = onSnapshot(q, snap => {
      const rows: ActiveSession[] = [];
      snap.docs.forEach(d => {
        const data = d.data();
        const gs = data.gameState || {};
        const players: any[] = gs.players || [];
        const currentIdx = gs.currentPlayerIndex;
        const currentP = (typeof currentIdx === 'number' && currentIdx >= 0 && currentIdx < players.length)
          ? players[currentIdx] : null;
        const handInProgress = gs.status === 'playing' && gs.round && gs.round !== 'ended';
        rows.push({
          tableId: d.id,
          name: data.name || d.id,
          isTournament: !!data.isTournament || !!data.tournamentId,
          tournamentId: data.tournamentId,
          status: gs.status || data.status || 'waiting',
          role: 'spectating',
          myChips: 0,
          myTurn: false,
          currentPlayerName: currentP?.name,
          handInProgress: !!handInProgress,
        });
      });
      setSpectating(rows);
    }, (err) => {
      // Index missing or no mirror field - degrade silently. Seated tables
      // still work; this just means spectator sessions won't appear in the
      // dock until the server populates spectatorUserIds.
      console.warn('[useMyActiveSessions] spectating subscription error:', err);
      setSpectating([]);
    });
    return () => unsub();
  }, [currentUserId]);

  // Merge, de-dupe by tableId (prefer seated over spectating), sort.
  const seen = new Set<string>();
  const merged: ActiveSession[] = [];
  for (const s of seated) { merged.push(s); seen.add(s.tableId); }
  for (const s of spectating) { if (!seen.has(s.tableId)) merged.push(s); }
  merged.sort((a, b) => {
    if (a.myTurn !== b.myTurn) return a.myTurn ? -1 : 1;
    if (a.role !== b.role) return a.role === 'seated' ? -1 : 1;
    if (a.isTournament !== b.isTournament) return a.isTournament ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return merged;
}

/** @deprecated - kept temporarily for existing callers. */
export type MySeatedTable = ActiveSession;
export const useMySeatedTables = useMyActiveSessions;
